/**
 * Human-readable labels for permission keys (Admin user form picker).
 */

function titleCaseWords(code: string): string {
  return code
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const NAME_OVERRIDES: Record<string, string> = {
  pos_manage: 'POS Manage',
  vendor_product_manage: 'Manage vendor products',
  purchase_create: 'Create purchase order',
  purchase_read: 'View purchase orders',
  purchase_update: 'Update purchase order',
  purchase_delete: 'Delete purchase order',
  purchase_send: 'Send purchase order',
  goods_receive: 'Goods receive',
  goods_receive_complete: 'Complete goods receive',
  stock_movement_create: 'Stock movement create',
  stock_adjustment_create: 'Stock adjustment create',
  stock_adjustment_approve: 'Stock adjustment approve',
  stock_transfer_create: 'Stock transfer create',
  stock_transfer_receive: 'Stock transfer receive',
  locations_view_all: 'View all company locations (reports / PO / POS)',
  dashboard_view: 'Dashboard view',
  view_analytics: 'View analytics',
  report_view: 'Sales report view',
  report_export: 'Sales report export',
  edit_content: 'Edit content',
  manage_users: 'Manage users',
  manage_settings: 'Manage settings',
};

/** Shown first in the Admin user permissions picker; backend treats `all` / `*` as full access (still plan-gated). */
export const ALL_PERMISSION_ITEM: PermissionPickerItem = {
  code: 'all',
  group: 'Shortcuts',
  name: 'All permissions',
};

export function inferPermissionGroup(code: string): string {
  if (code === 'all' || code === '*') return 'Shortcuts';
  if (code === 'pos_manage') return 'Point of Sale';
  if (code.startsWith('product_')) return 'Products';
  if (code.startsWith('category_')) return 'Categories';
  if (code.startsWith('vendor_')) return 'Vendors';
  // Club Sales CRUD + Purchase Orders in one operational group.
  if (code.startsWith('sales_')) return 'Sales & Purchase operations';
  if (code.startsWith('purchase_')) return 'Sales & Purchase operations';
  if (code.startsWith('goods_receive')) return 'Sales & Purchase operations';
  if (code.startsWith('stock_')) return 'Stock';
  if (code.startsWith('inventory_')) return 'Inventory';
  if (code.startsWith('user_')) return 'Users';
  if (code.startsWith('role_')) return 'Roles';
  if (code.startsWith('location_')) return 'Locations';
  if (code === 'locations_view_all') return 'Locations';
  if (code.startsWith('document_')) return 'Documents';
  // Keep reports separated from operational CRUD permissions.
  if (code.startsWith('report_')) return 'Sales reports';
  if (code === 'dashboard_view') return 'Dashboard';
  if (['edit_content', 'manage_users', 'manage_settings', 'view_analytics'].includes(code)) {
    return 'General';
  }
  return 'Other';
}

export function getPermissionDisplay(code: string): { group: string; name: string } {
  if (code === 'all' || code === '*') {
    return { group: 'Shortcuts', name: 'All permissions' };
  }
  return {
    group: inferPermissionGroup(code),
    name: NAME_OVERRIDES[code] ?? titleCaseWords(code),
  };
}

export type PermissionPickerItem = { code: string; group: string; name: string };

export function sortPermissionPickerItems(items: PermissionPickerItem[]): PermissionPickerItem[] {
  return [...items].sort((a, b) => {
    const g = a.group.localeCompare(b.group);
    if (g !== 0) return g;
    return a.name.localeCompare(b.name);
  });
}
